import React, { useMemo, useState } from "react";
import dayjs from "dayjs";

import {
  acknowledgeAnnouncementForUser,
  trackAnnouncementResourceClickForUser,
} from "../utils/announcementService";
import { getAnnouncementAction } from "../utils/announcementDisplayUtils";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";

import "../../../../css/Announcements/AnnouncementPopup.css";
import { useUserEmail } from "../../../../context/UserEmailContext";

const normaliseValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const formatPostedDate = (announcement) => {
  const date =
    announcement?.postedDate ||
    announcement?.scheduleDate ||
    announcement?.createdAt?.toDate?.();

  return date ? dayjs(date).format("DD MMM YY") : "-";
};

const AnnouncementPopup = ({
  announcements = [],
  acknowledgedIds = [],
  // userEmail = "",
  userRole = "",
  userName = "",
}) => {
  const [acknowledging, setAcknowledging] = useState(false);
  const [localAcknowledgedIds, setLocalAcknowledgedIds] = useState([]);
  const {userEmail} = useUserEmail();

  const safeUserEmail = normaliseValue(userEmail);

  const acknowledgedSet = useMemo(() => {
    return new Set(
      [...acknowledgedIds, ...localAcknowledgedIds]
        .map((id) => String(id || "").trim())
        .filter(Boolean),
    );
  }, [acknowledgedIds, localAcknowledgedIds]);

  const announcementToShow = useMemo(() => {
    return announcements.find((announcement) => {
      return (
        announcement?.id &&
        announcement?.notifyOnLogin === true &&
        !acknowledgedSet.has(announcement.id)
      );
    });
  }, [announcements, acknowledgedSet]);

  if (!announcementToShow) return null;

  const headerImage = announcementToShow.headerImageFiles?.[0];
  const action = getAnnouncementAction(announcementToShow);

  const handleOpenAction = () => {
    if (!action?.url) return;

    trackAnnouncementResourceClickForUser({
      announcementId: announcementToShow?.id,
      userEmail: safeUserEmail,
      userRole,
      userName,
      source: "popup",
      actionType: action?.type || "url",
      actionUrl: action.url,
    }).catch((error) => {
      console.error("[AnnouncementPopup] Failed to track read more click:", error);
    });

    window.open(action.url, "_blank", "noopener,noreferrer");
  };

  const handleAcknowledge = async () => {
    if (acknowledging) return;

    if (!announcementToShow?.id || !safeUserEmail) {
      console.error("[AnnouncementPopup] Missing acknowledgement data", {
        announcementId: announcementToShow?.id,
        userEmail,
      });
      return;
    }

    try {
      setAcknowledging(true);

      await acknowledgeAnnouncementForUser({
        announcementId: announcementToShow.id,
        userEmail: safeUserEmail,
        userRole,
        userName,
      });

      setLocalAcknowledgedIds((prev) =>
        Array.from(new Set([...prev, announcementToShow.id])),
      );
    } catch (error) {
      console.error("[AnnouncementPopup] Failed to acknowledge:", error);
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div className="announcement-popup-backdrop">
      <section className="announcement-popup-card">
        <div className="FP_Subtitle_Semibold">
          {announcementToShow.contentTitle || "Announcement"}
        </div>

        {headerImage?.url && (
          <div className="announcement-popup-image-wrap">
            <img
              src={headerImage.url}
              alt={announcementToShow.contentTitle || "Announcement"}
              className="announcement-popup-image"
            />
          </div>
        )}

        <div className="announcement-popup-meta-row">
          <div className="FP_Help_Text uppercase">
            {announcementToShow.contentType || "Announcement"}
          </div>

          <span className="FP_Help_Text black">
            {formatPostedDate(announcementToShow)}
          </span>
        </div>

        <div className="FP_Content grey">
          {announcementToShow.content || ""}
        </div>

        {action && (
          <PrimaryButton
            type="button"
            className="announcement-popup-resource-button"
            onClick={handleOpenAction}
            label="Read more"
          />
        )}

        <PrimaryButton
          type="button"
          onClick={handleAcknowledge}
          disabled={acknowledging}
          label={acknowledging ? "Acknowledging..." : "Acknowledge"}
        />
      </section>
    </div>
  );
};

export default AnnouncementPopup;
